import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  ApiFilterOperation,
  DetailContext,
  DetailMode,
  FieldFilter,
  FormAutocomplete,
  FormDateTimeField,
  FormDictionaryField,
  FormPanel,
  FormSelect,
  FormTextArea,
  FormTextField,
  TableFieldCells,
  eqFilterParams,
  useFormSelector,
  useScrollableSource,
  useStaticListSource,
} from '@eas/common-web';
import { getRolesForAgenda } from '@modules/es-file/es-file-permission';
import { useUsers } from '@modules/user/user-api';
import { getDocumentUrl } from '@composite/documents/document-columns';
import { Redirect } from '@composite/redirect/redirect';
import { AuthoredFields } from '@components/evidence/authored-evidence/authored-fields';
import { StaticDataContext } from '@components/evidence/static-data-provider/static-data-provider';
import {
  autocompleteLabelMapper,
  dictionaryFieldLabelMapper,
} from '@components/form/misc/label-mappers';
import { Decision, Document, Task } from '@models';
import {
  DocumentType,
  EvidenceApiUrl,
  EvidenceBrowserUrl,
  Role,
  TaskType,
} from '@enums';
import { useDecisions } from '../decision/decision-api';
import { useTaskNames } from '../dict-task-name/dict-task-name-api';
import { useDocuments } from '../document/document-api';

export function Fields() {
  const TasksFields = useTasksFields();

  return (
    <>
      <FormPanel
        label={
          <FormattedMessage
            id="ES__TASKS___DETAIL__TASK_PANEL_TITLE"
            defaultMessage="Úloha"
          />
        }
      >
        <TasksFields />
      </FormPanel>
    </>
  );
}

export function useTasksFields() {
  const {
    taskTypes,
    taskStates,
    documentTypes,
    decisionForms,
    decisionStates,
  } = useContext(StaticDataContext);

  return function TasksFields() {
    const { mode, source } = useContext(DetailContext);
    const { decision, document, esFile, type } = useFormSelector(
      (data: Task) => ({
        decision: data?.decision,
        document: data?.document,
        esFile: data?.esFile,
        type: data?.type,
      })
    );

    const isSystemTask = type === TaskType.SYSTEM;

    const typesSource = useStaticListSource(taskTypes);
    const stateSource = useStaticListSource(taskStates);
    const useDocumentTypes = () => useStaticListSource(documentTypes);
    const useDecisionForms = () => useStaticListSource(decisionForms);
    const useDecisionStates = () => useStaticListSource(decisionStates);

    const taskNames = useTaskNames();
    const documents = useDocuments(
      eqFilterParams({ field: 'esFile.id', value: esFile?.id })
    );
    const decisions = useDecisions(
      eqFilterParams({ field: 'esFile.id', value: esFile?.id })
    );
    const documentsSource = useScrollableSource<Document>({
      url: `${EvidenceApiUrl.DOCUMENTS}/list`,
    });
    const decisionsSource = useScrollableSource<Decision>({
      url: `${EvidenceApiUrl.DECISIONS}/list`,
    });
    const agenda = source?.data?.agenda ?? source?.data?.esFile?.agenda ?? null;

    const accessibleRoles = agenda ? getRolesForAgenda(agenda) : [];
    const otherRoles = accessibleRoles.filter(
      (role) => ![Role.INDEX, Role.READER, Role.SOLVER].includes(role)
    );

    const solverUsers = useUsers({
      filters: [
        {
          operation: ApiFilterOperation.OR,
          filters: [
            {
              operation: ApiFilterOperation.AND,
              filters: [
                {
                  operation: ApiFilterOperation.IN,
                  field: 'userRoles.role.id',
                  values: [Role.INDEX, Role.READER, Role.SOLVER],
                },
                {
                  operation: ApiFilterOperation.IN,
                  field: 'userRoles.agendas.id',
                  values: agenda ? [agenda] : [],
                },
              ],
            },
            {
              operation: ApiFilterOperation.AND,
              filters: [
                {
                  operation: ApiFilterOperation.IN,
                  field: 'userRoles.role.id',
                  values: otherRoles,
                },
              ],
            },
          ],
        },
      ],
    });

    return (
      <>
        <FormAutocomplete
          name="name"
          source={taskNames}
          label={
            <FormattedMessage
              id="ES__TASKS__DETAIL__FIELD_LABEL__NAME"
              defaultMessage="Název"
            />
          }
          labelMapper={autocompleteLabelMapper}
          disabled={isSystemTask}
          required={true}
          freeSolo={true}
          autoFocus={true}
        />
        <FormTextArea
          name="description"
          label={
            <FormattedMessage
              id="ES__TASKS__DETAIL__FIELD_LABEL__DESCRIPTION"
              defaultMessage="Popis"
            />
          }
          disabled={isSystemTask}
        />
        <FormSelect
          name="type"
          source={typesSource}
          label={
            <FormattedMessage
              id="ES__TASKS__DETAIL__FIELD_LABEL__TYPE"
              defaultMessage="Typ úlohy"
            />
          }
          disabled={true}
          valueIsId={true}
          required={true}
        />
        <FormSelect
          name="state"
          source={stateSource}
          label={
            <FormattedMessage
              id="ES__TASKS__DETAIL__FIELD_LABEL__STATE"
              defaultMessage="Stav úlohy"
            />
          }
          disabled={true}
          valueIsId={true}
          required={true}
        />
        {esFile?.number && (
          <FormTextField
            name="esFile.number.value"
            label={
              <FormattedMessage
                id="ES__TASKS__DETAIL__FIELD_LABEL__FILE_NUMBER"
                defaultMessage="Spisová značka"
              />
            }
            after={
              esFile?.id && (
                <Redirect
                  url={EvidenceBrowserUrl.ES_FILES_ALL}
                  id={esFile.id}
                />
              )
            }
            disabled={true}
            required={true}
          />
        )}
        <FormDictionaryField<Document>
          name="document"
          label={
            <FormattedMessage
              id="ES__TASKS__DETAIL__FIELD_LABEL__REFERENCE_NUMBER"
              defaultMessage="Číslo jednací"
            />
          }
          dialogSource={documentsSource}
          autocompleteSource={documents}
          columns={[
            {
              name: 'Číslo jednací',
              datakey: 'referenceNumber',
              width: 300,
            },
            { name: 'Stručný obsah', datakey: 'summary', width: 300 },
            {
              name: 'Typ dokumentu',
              datakey: 'type',
              width: 300,
              CellComponent:
                TableFieldCells.useSelectCellFactory(useDocumentTypes),
            },
            {
              name: 'Datum',
              datakey: 'date',
              width: 300,
              CellComponent: TableFieldCells.DateCell,
            },
          ]}
          filters={[
            {
              label: 'Číslo jednací',
              datakey: 'name',
              filterkey: 'referenceNumber',
              defaultOperation: ApiFilterOperation.CONTAINS,
              FilterComponent: FieldFilter.FilterTextCell,
            },
            {
              label: 'Stručný obsah',
              datakey: 'summary',
              filterkey: 'summary',
              defaultOperation: ApiFilterOperation.CONTAINS,
              FilterComponent: FieldFilter.FilterTextCell,
            },
            {
              label: 'Typ dokumentu',
              datakey: 'type',
              filterkey: 'type.id',
              defaultOperation: ApiFilterOperation.EQ,
              FilterComponent: FieldFilter.FilterSelectCell(useDocumentTypes),
            },
            {
              label: 'Datum',
              datakey: 'date',
              filterkey: 'date',
              defaultOperation: ApiFilterOperation.EQ,
              FilterComponent: FieldFilter.FilterDateCell,
            },
          ]}
          labelMapper={dictionaryFieldLabelMapper}
          after={
            document?.id &&
            document.referenceNumber && (
              <Redirect
                url={getDocumentUrl(document.type ?? DocumentType.DELIVERED)}
                id={document.id}
              />
            )
          }
          dialogPreFilters={[
            {
              field: 'esFile.id',
              operation: ApiFilterOperation.EQ,
              value: esFile?.id,
            },
          ]}
          disabled={
            mode !== DetailMode.NEW ||
            source.url === EvidenceApiUrl.DOCUMENTS ||
            isSystemTask
          }
        />
        <FormDictionaryField<Decision>
          name="decision"
          label={
            <FormattedMessage
              id="ES__TASKS__DETAIL__FIELD_LABEL__DECISION_NUMBER"
              defaultMessage="Číslo rozhodnutí"
            />
          }
          dialogSource={decisionsSource}
          autocompleteSource={decisions}
          columns={[
            {
              name: 'Číslo rozhodnutí',
              datakey: 'number',
              width: 300,
            },
            {
              name: 'Forma rozhodnutí',
              datakey: 'form',
              width: 300,
              CellComponent:
                TableFieldCells.useSelectCellFactory(useDecisionForms),
            },
            {
              name: 'Datum rozhodnutí',
              datakey: 'decisionDate',
              width: 300,
              CellComponent: TableFieldCells.DateCell,
            },
            {
              name: 'Stav',
              datakey: 'state',
              width: 300,
              CellComponent:
                TableFieldCells.useSelectCellFactory(useDecisionStates),
            },
          ]}
          filters={[
            {
              label: 'Číslo rozhodnutí',
              datakey: 'number',
              filterkey: 'number',
              defaultOperation: ApiFilterOperation.CONTAINS,
              FilterComponent: FieldFilter.FilterTextCell,
            },
            {
              label: 'Forma rozhodnutí',
              datakey: 'form',
              filterkey: 'form.id',
              defaultOperation: ApiFilterOperation.EQ,
              FilterComponent: FieldFilter.FilterSelectCell(useDecisionForms),
            },
            {
              label: 'Datum rozhodnutí',
              datakey: 'decisionDate',
              filterkey: 'decisionDate',
              defaultOperation: ApiFilterOperation.EQ,
              FilterComponent: FieldFilter.FilterDateCell,
            },
            {
              label: 'Stav',
              datakey: 'state',
              filterkey: 'state.id',
              defaultOperation: ApiFilterOperation.EQ,
              FilterComponent: FieldFilter.FilterSelectCell(useDecisionStates),
            },
          ]}
          labelMapper={(value) =>
            Array.isArray(value)
              ? value.map((v) => v?.number ?? v?.label ?? '').join(', ')
              : value?.number ?? value?.label ?? ''
          }
          after={
            decision?.id && (
              <Redirect
                url={EvidenceBrowserUrl.DECISIONS_ALL}
                id={decision.id}
              />
            )
          }
          dialogPreFilters={[
            {
              field: 'esFile.id',
              operation: ApiFilterOperation.EQ,
              value: esFile?.id,
            },
          ]}
          disabled={mode !== DetailMode.NEW || !esFile || isSystemTask}
        />
        <FormAutocomplete
          name="solver"
          source={solverUsers}
          label={
            <FormattedMessage
              id="ES__TASKS__DETAIL__FIELD_LABEL__SOLVER"
              defaultMessage="Pro uživatele"
            />
          }
          disabled={!esFile || mode === DetailMode.VIEW}
          required={true}
          labelMapper={autocompleteLabelMapper}
        />
        <FormDateTimeField
          name="created"
          label={
            <FormattedMessage
              id="ES__TASKS__DETAIL__FIELD_LABEL__CREATED"
              defaultMessage="Datum vytvoření"
            />
          }
          disabled={true}
        />
        <AuthoredFields.CreatedBy />
      </>
    );
  };
}
